// ### VARIABLES INITIALIZATION
import { PROPERTY_TYPE_DEFAULT } from '@/config/property-types';
import { stripUrlQueryForIndexing } from '@/utils/stripUrlQueryForIndexing.js';

export function generateSeo() {
  const { t } = useI18n();
  const propertyTypeObj = PROPERTY_TYPE_DEFAULT;

  const getPageTypeSeoTitle = (pageType) => {
    switch (pageType) {
      case 'homepage':
        return {
          title: t('t_DEFAULTSEOHOMEPAGETITLE'),
          description: t('t_DEFAULTSEOHOMEPAGEDESCRIPTION'),
        };
      case 'worldwide':
        return {
          title: t('t_DEFAULTSEOWORLDWIDETITLE'),
          description: t('t_DEFAULTSEOWORLDWIDEDESCRIPTION'),
        };
      case 'continent':
        return {
          title: t('t_DEFAULTSEOCONTINENTTITLE'),
          description: t('t_DEFAULTSEOCONTINENTDESCRIPTION'),
        };
      case 'country':
        return {
          title: t('t_DEFAULTSEOCOUNTRYTITLE'),
          description: t('t_DEFAULTSEOCOUNTRYDESCRIPTION'),
        };
      case 'area':
        return {
          title: t('t_DEFAULTSEOAREATITLE'),
          description: t('t_DEFAULTSEOAREADESCRIPTION'),
        };
      case 'region':
        return {
          title: t('t_DEFAULTSEOREGIONTITLE'),
          description: t('t_DEFAULTSEOREGIONDESCRIPTION'),
        };
      case 'city':
        return {
          title: t('t_DEFAULTSEOCITYTITLE'),
          description: t('t_DEFAULTSEOCITYDESCRIPTION'),
        };
      case 'district':
        return {
          title: t('t_DEFAULTSEODISTRICTTITLE'),
          description: t('t_DEFAULTSEODISTRICTDESCRIPTION'),
        };
      case 'property':
        return {
          title: t('t_DEFAULTSEOPROPERTYPAGETITLE'),
          description: t('t_DEFAULTSEOPROPERTYPAGEDESCRIPTION'),
        };
      case 'generic':
        return {
          title: t('t_DEFAULTSEOGENERICTITLE'),
          description: t('t_DEFAULTSEOGENERICDESCRIPTION'),
        };
      case 'eventsworldwide':
      case 'eventscontinent':
      case 'eventscountry':
      case 'eventscity':
        return {
          title: t('t_SEOTITLEEVENTS', { YEAR: new Date().getFullYear() }),
          description: t('t_SEODESCRIPTIONEVENTS', { YEAR: new Date().getFullYear() }),
        };
      default:
        return {
          title: t('t_DEFAULTSEOTITLE'),
          description: t('t_DEFAULTSEODESCRIPTION'),
        };
    }
  };

  const getMetaData = (seoData, extraData = {}, replacements = {}, isGeneric = false, pageType = null) => {
    if (isGeneric) {
      replacements = {
        PROPERTYTYPE: t(propertyTypeObj?.transcode_s),
        PROPERTYTYPES: t(propertyTypeObj?.transcode),
        YEAR: new Date().getFullYear(),
      };
    }

    const defaultSeoTags = getPageTypeSeoTitle(pageType);

    const title = (seoData?.title || seoData?.ogTitle)?.replace(/\r?\n|\r/g, '') || defaultSeoTags?.title;
    const ogTitle = (seoData?.title || seoData?.ogTitle)?.replace(/\r?\n|\r/g, '') || defaultSeoTags?.title;
    const description
      = (seoData?.description || seoData?.ogDescription)?.replace(/\r?\n|\r/g, '') || defaultSeoTags?.description;
    const ogDescription
      = (seoData?.description || seoData?.ogDescription)?.replace(/\r?\n|\r/g, '') || defaultSeoTags?.description;

    const ogImage = extraData?.ogImage
      ? extraData?.ogImage
      : 'https://a.hwstatic.com/image/upload/f_auto,q_auto,h_600/v1647449187/pwa/hostelworld.png';

    const copy = seoData?.copy && seoData?.copy !== '<p><br></p>' ? seoData?.copy : null;
    const copy2 = seoData?.copy2 && seoData?.copy2 !== '<p><br></p>' ? seoData?.copy2 : null;

    let fullCopy = copy;
    if (copy && copy2) {
      fullCopy += `<br />${copy2}`;
    }

    const result = {
      title,
      ogTitle,
      description,
      ogDescription,
      canonicalUrl: stripUrlQueryForIndexing(extraData?.canonicalUrl),
      ogImage,
      keywords: seoData?.keywords || '',
      copy: fullCopy,
      heading: seoData?.heading || null,
      h1: seoData?.h1 || null,
      h2: seoData?.h2 || null,
    };

    const transcodeInterpolate = new TranscodeInterpolate();
    const propertiesToReplace = [
      'title',
      'description',
      'copy',
      'ogTitle',
      'ogDescription',
      'heading',
      'keywords',
      'h1',
      'h2',
    ];
    propertiesToReplace.forEach((property) =>
      result?.[property]
        ? (result[property] = transcodeInterpolate.interpolate(result[property], replacements))
        : (result[property] = ''),
    );

    return result;
  };

  return { buildSeo: { getMetaData } };
}
